import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { httpRequestsOnSuccessActions, invoiceActions, invoicePaymentActions, tagActions } from 'store';
import { AccordionFullTable, Aging, CustomDelete, SelectActions } from 'components';
import { FindLoad, FindSuccessItem, Images, tagEnums, useModal } from 'utils';
import { invoiceStatusEnums, openInvoiceTableInfo, PAY_IN_FULL_PAYMENT_INVOICE, DELETE_OPEN_INVOICES_TYPE } from './constants';
import { AddTransactionModal } from './core';
import { Skeleton } from '@mui/material';

const GET_ACTION_TYPE = 'GET_INVOICE_PAYMENTS';

export const OpenInvoice = () => {
    const invoicePaymentsData = useSelector((state) => state.invoicePayment.invoicePayments);
    const invoiceTotals = useSelector((state) => state.invoicePayment.invoiceTotals);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const { open, close } = useModal();
    const loader = FindLoad(GET_ACTION_TYPE);
    const payInFullSuccess = FindSuccessItem(PAY_IN_FULL_PAYMENT_INVOICE);
    const deleteSuccess = FindSuccessItem(DELETE_OPEN_INVOICES_TYPE);
    const [selectedList, setSelectedList] = useState([]);
    const [payInFullSelectedId, setPayInFullSelectedId] = useState(null);

    const renderParams = () => {
        const pushInfo = {
            ...info,
            statuses: [invoiceStatusEnums.OPEN],
        };
        delete pushInfo.page;
        delete pushInfo.tabType;
        delete pushInfo.clientFull;
        pushInfo.skip = info?.skip ? info?.skip : 0;
        pushInfo.limit = 50;
        return pushInfo;
    };

    const renderTotalsParams = () => {
        const pushInfo = {
            ...info,
            statuses: [invoiceStatusEnums.OPEN],
        };
        delete pushInfo.page;
        delete pushInfo.tabType;
        delete pushInfo.clientFull;
        delete pushInfo.skip;
        delete pushInfo.limit;
        return pushInfo;
    };

    const getInvoices = (load) => {
        dispatch(invoicePaymentActions.getInvoicePayments({ ...renderParams() }, load));
        dispatch(invoicePaymentActions.getInvoiceTotals({ ...renderTotalsParams() }));
    };

    useEffect(() => {
        getInvoices();
        dispatch(tagActions.getTags({ module: tagEnums.INVOICE }));
    }, [info]);

    useEffect(() => {
        if (deleteSuccess) {
            getInvoices('noLoad');
            setSelectedList([]);
            dispatch(httpRequestsOnSuccessActions.removeSuccess(DELETE_OPEN_INVOICES_TYPE));
        }
        if (payInFullSuccess) {
            getInvoices('noLoad');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(PAY_IN_FULL_PAYMENT_INVOICE));
        }
    }, [deleteSuccess, payInFullSuccess]);

    const handleCheck = (ev, item, name) => {
        ev.stopPropagation();
        ev.preventDefault();
        if (name === 'all') {
            setSelectedList(
                invoicePaymentsData?.invoices?.length === selectedList?.length
                    ? []
                    : invoicePaymentsData?.invoices?.map((i) => i?.id)
            );
        } else {
            setSelectedList((prev) =>
                prev.includes(item?.id) ? prev.filter((id) => id !== item?.id) : [...prev, item?.id]
            );
        }
    };

    const removeSelectedList = () => {
        setSelectedList([]);
    };


    const handleAction = (item, actionName) => {
        if (actionName === 'payInFull') {
            setPayInFullSelectedId(item?.id);
            dispatch(invoicePaymentActions.payInFullInvoice(item?.id, {}));
        }
        if (actionName === 'addTxn') {
            open(
                <AddTransactionModal
                    invoice={item}
                    afterSuccess={() => {
                        close();
                        getInvoices('noLoad');
                    }}
                />
            );
        }
        if (actionName === 'delete') {
            open(
                <CustomDelete
                    info="Delete Invoice?"
                    text="Are you sure you want to delete this invoice? Bills will be moved back to Invoiceable Appts."
                    handleDel={() => dispatch(invoiceActions.deleteOpenInvoice(item?.id))}
                    actionType={'DELETE_OPEN_INVOICE'}
                    afterSuccess={() => {
                        close();
                        getInvoices('noLoad');
                    }}
                    innerText="Delete"
                />
            );
        }
    };

    const handleBulkDelete = () => {
        open(
            <CustomDelete
                info="Delete Open Invoices?"
                text="Are you sure you want to delete the selected invoices? Invoices with active transactions will be skipped."
                handleDel={() => dispatch(invoiceActions.deleteOpenInvoicesBulk(selectedList))}
                actionType={DELETE_OPEN_INVOICES_TYPE}
                afterSuccess={() => {
                    close();
                    getInvoices('noLoad');
                    setSelectedList([]);
                }}
                innerText="Delete"
            />
        );
    };

    return (
        <div>
            {loader?.length ? (
                <div style={{ margin: '0 0 6px 0' }}>
                    <Skeleton variant="rectangular" width={'100%'} height={60} style={{ borderRadius: '8px' }} />
                </div>
            ) : (
                <Aging aging={invoicePaymentsData?.aging} totals={invoiceTotals} />
            )}

            {!!selectedList?.length && (
                <SelectActions
                    noSubmit={true}
                    selectedList={selectedList?.length}
                    selectAll={handleCheck}
                    removeSelected={removeSelectedList}
                    renderOtherBtns={
                        <button onClick={handleBulkDelete} className="red-trash-btn">
                            <img src={Images.redTrashLine} alt="delete" />
                        </button>
                    }
                />
            )}

            <AccordionFullTable
                margin={'6px 0 0 0'}
                tableInfo={openInvoiceTableInfo(
                    navigate,
                    info,
                    handleCheck,
                    handleAction,
                    invoicePaymentsData?.invoices,
                    selectedList,
                    payInFullSelectedId,
                    renderParams,
                )}
                list={invoicePaymentsData?.invoices}
                listCount={invoicePaymentsData?.count}
                detailPage={(item) => `/invoice/${item?.id}`}
                selectedList={selectedList}
            />
        </div>
    );
};
